/**
 * NotaryDossiersListPage — Multi-suivi des dossiers notaire (P2 batch 2).
 * Vue tableau : nom vendeur · adresse · date cible · avancement · statut.
 * Bouton "Nouveau dossier" qui crée un dossier avec checklist standard.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FolderClosed, Search, Filter, Loader2, ChevronRight, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ReseauLayout from '../../components/reseau/ReseauLayout';

function StatusPill({ status }) {
    const map = {
        open: { label: 'En cours', color: '#681793', bg: 'rgba(104,23,147,0.10)' },
        ready: { label: 'Prêt notaire', color: '#0f766e', bg: 'rgba(15,118,110,0.12)' },
        sent_to_notary: { label: 'Envoyé notaire', color: '#0369a1', bg: 'rgba(3,105,161,0.12)' },
        closed: { label: 'Clos', color: '#4b5563', bg: 'rgba(75,85,99,0.12)' },
        archived: { label: 'Archivé', color: '#78716c', bg: 'rgba(120,113,108,0.12)' },
    };
    const s = map[status] || map.open;
    return (
        <span data-testid={`dossier-status-${status}`}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
              style={{ color: s.color, background: s.bg }}>
            {s.label}
        </span>
    );
}

function ProgressBar({ percent, complete }) {
    return (
        <div className="w-full h-1.5 rounded-full overflow-hidden"
             style={{ background: 'rgba(0,0,0,0.06)' }}>
            <div className="h-full transition-all"
                 style={{
                     width: `${percent || 0}%`,
                     background: complete ? '#16a34a' : 'var(--reseau-violet, #681793)',
                 }} />
        </div>
    );
}

function CreateDossierDialog({ open, onClose, onCreated }) {
    const [form, setForm] = useState({
        seller_name: '', property_address: '', city: '', postal_code: '',
        target_signing_date: '', is_copro: true, notes: '',
    });
    const [saving, setSaving] = useState(false);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.seller_name.trim() || !form.property_address.trim()) {
            toast.error('Nom du vendeur et adresse requis');
            return;
        }
        setSaving(true);
        try {
            const r = await api.post('/notary/dossiers', {
                ...form,
                target_signing_date: form.target_signing_date || null,
            });
            toast.success('Dossier créé — checklist notaire pré-remplie');
            onCreated(r.data);
        } catch (e) {
            toast.error("Impossible de créer le dossier");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
             data-testid="dossier-create-dialog"
             onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
                  className="w-full max-w-lg rounded-2xl shadow-2xl p-6 space-y-3"
                  style={{ background: 'var(--card)', color: 'var(--reseau-ink)' }}>
                <div>
                    <h3 className="text-lg font-bold">Nouveau dossier notaire</h3>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--reseau-ink-muted)' }}>
                        La checklist standard sera pré-remplie automatiquement.
                    </p>
                </div>
                <div className="space-y-2">
                    <input value={form.seller_name}
                           onChange={(e) => setForm({ ...form, seller_name: e.target.value })}
                           placeholder="Nom du vendeur"
                           data-testid="dossier-input-seller"
                           className="w-full px-3 py-2 rounded-lg border text-sm"
                           style={{ borderColor: 'var(--border)' }} />
                    <input value={form.property_address}
                           onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                           placeholder="Adresse du bien"
                           data-testid="dossier-input-address"
                           className="w-full px-3 py-2 rounded-lg border text-sm"
                           style={{ borderColor: 'var(--border)' }} />
                    <div className="grid grid-cols-3 gap-2">
                        <input value={form.postal_code}
                               onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                               placeholder="CP"
                               data-testid="dossier-input-postal"
                               className="col-span-1 px-3 py-2 rounded-lg border text-sm"
                               style={{ borderColor: 'var(--border)' }} />
                        <input value={form.city}
                               onChange={(e) => setForm({ ...form, city: e.target.value })}
                               placeholder="Ville"
                               data-testid="dossier-input-city"
                               className="col-span-2 px-3 py-2 rounded-lg border text-sm"
                               style={{ borderColor: 'var(--border)' }} />
                    </div>
                    <div>
                        <label className="text-xs font-medium block mb-1"
                               style={{ color: 'var(--reseau-ink-muted)' }}>
                            Date cible de signature
                        </label>
                        <input type="date" value={form.target_signing_date}
                               onChange={(e) => setForm({ ...form, target_signing_date: e.target.value })}
                               data-testid="dossier-input-target-date"
                               className="w-full px-3 py-2 rounded-lg border text-sm"
                               style={{ borderColor: 'var(--border)' }} />
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer"
                           data-testid="dossier-input-copro-label">
                        <input type="checkbox" checked={form.is_copro}
                               onChange={(e) => setForm({ ...form, is_copro: e.target.checked })}
                               data-testid="dossier-input-copro" />
                        <span>Bien en copropriété (ajoute règlement, PV AG, charges…)</span>
                    </label>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose}
                            className="text-sm px-3 py-2 rounded-lg"
                            style={{ color: 'var(--reseau-ink-muted)' }}>
                        Annuler
                    </button>
                    <button type="submit" disabled={saving}
                            data-testid="dossier-create-submit"
                            className="px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5"
                            style={{ background: 'var(--reseau-violet, #681793)', color: '#fff' }}>
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        Créer le dossier
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function NotaryDossiersListPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    const [creating, setCreating] = useState(false);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter && statusFilter !== 'active' && statusFilter !== 'all') {
                params.set('status', statusFilter);
            }
            const r = await api.get(`/notary/dossiers?${params.toString()}`);
            setItems(r.data.items || []);
        } catch (e) {
            toast.error('Impossible de charger les dossiers');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const visibleItems = useMemo(() => {
        let arr = items;
        if (statusFilter === 'active') arr = arr.filter((d) => d.status === 'open' || d.status === 'ready');
        if (q.trim()) {
            const s = q.trim().toLowerCase();
            arr = arr.filter((d) =>
                (d.seller_name || '').toLowerCase().includes(s) ||
                (d.property_address || '').toLowerCase().includes(s) ||
                (d.city || '').toLowerCase().includes(s)
            );
        }
        return arr;
    }, [items, statusFilter, q]);

    return (
        <ReseauLayout headerTitle="Dossiers notaire"
                      headerSubtitle="Suivez la constitution complète de chaque dossier de vente jusqu'à la signature.">
            <main className="mx-auto px-4 sm:px-6 py-6 space-y-4"
                  data-testid="notary-dossiers-list"
                  style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                <div className="flex items-center gap-2 flex-wrap justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-[260px] max-w-md">
                        <div className="relative w-full">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2"
                                    style={{ color: 'var(--reseau-ink-muted)' }} />
                            <input value={q} onChange={(e) => setQ(e.target.value)}
                                   placeholder="Rechercher un vendeur, une adresse…"
                                   data-testid="dossier-search"
                                   className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm"
                                   style={{ borderColor: 'var(--border)', background: 'var(--card)' }} />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <select value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                data-testid="dossier-filter-status"
                                className="text-sm px-3 py-2 rounded-lg border"
                                style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
                            <option value="active">En cours ou prêts</option>
                            <option value="all">Tous</option>
                            <option value="open">En cours</option>
                            <option value="ready">Prêts notaire</option>
                            <option value="sent_to_notary">Envoyés notaire</option>
                            <option value="closed">Clos</option>
                            <option value="archived">Archivés</option>
                        </select>
                        <button type="button" onClick={() => setCreating(true)}
                                data-testid="dossier-create-btn"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold"
                                style={{ background: 'var(--reseau-violet, #681793)', color: '#fff' }}>
                            <Plus className="w-4 h-4" /> Nouveau dossier
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-10">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto"
                                  style={{ color: 'var(--reseau-violet)' }} />
                    </div>
                ) : visibleItems.length === 0 ? (
                    <div className="reseau-empty text-center py-14 rounded-2xl"
                         data-testid="dossier-empty"
                         style={{ background: 'var(--card)' }}>
                        <FolderClosed className="w-10 h-10 mx-auto mb-3"
                                      style={{ color: 'var(--reseau-ink-faint, #cbd5e1)' }} />
                        <h3 className="text-base font-semibold">Aucun dossier notaire</h3>
                        <p className="text-sm mt-1 mb-4"
                           style={{ color: 'var(--reseau-ink-muted)' }}>
                            Créez un dossier pour chaque vente en cours. La checklist des 18 pièces
                            standard est pré-remplie automatiquement.
                        </p>
                        <button type="button" onClick={() => setCreating(true)}
                                data-testid="dossier-empty-cta"
                                className="reseau-btn reseau-btn-primary px-4 py-2 rounded-lg text-sm font-semibold"
                                style={{ background: 'var(--reseau-violet)', color: '#fff' }}>
                            Créer mon premier dossier
                        </button>
                    </div>
                ) : (
                    <ul className="space-y-2" data-testid="dossier-items">
                        {visibleItems.map((d) => {
                            const target = d.target_signing_date
                                ? new Date(d.target_signing_date).toLocaleDateString('fr-FR')
                                : null;
                            const p = d.progress || {};
                            return (
                                <li key={d.id}
                                    data-testid={`dossier-row-${d.id}`}
                                    onClick={() => navigate(`/notaire/dossiers/${d.id}`)}
                                    className="p-4 rounded-xl border cursor-pointer hover:shadow transition"
                                    style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="text-sm font-bold truncate"
                                                   style={{ color: 'var(--reseau-ink)' }}>
                                                    {d.seller_name}
                                                </p>
                                                <StatusPill status={d.status} />
                                            </div>
                                            <p className="text-xs mt-0.5 truncate"
                                               style={{ color: 'var(--reseau-ink-muted)' }}>
                                                {d.property_address}{d.city ? ` · ${d.city}` : ''}
                                            </p>
                                            {target && (
                                                <p className="text-[11px] mt-1 inline-flex items-center gap-1"
                                                   style={{ color: 'var(--reseau-ink-muted)' }}>
                                                    <CalendarClock className="w-3 h-3" />
                                                    Signature ciblée : {target}
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-right min-w-[100px]">
                                            <p className="text-xl font-extrabold tabular-nums"
                                               style={{ color: p.is_complete ? '#16a34a' : 'var(--reseau-ink)' }}
                                               data-testid={`dossier-progress-${d.id}`}>
                                                {p.required_percent || 0}%
                                            </p>
                                            <p className="text-[10px]"
                                               style={{ color: 'var(--reseau-ink-muted)' }}>
                                                {p.required_validated}/{p.required_total} pièces requises
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <ProgressBar percent={p.required_percent}
                                                     complete={p.is_complete} />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}

                <CreateDossierDialog
                    open={creating}
                    onClose={() => setCreating(false)}
                    onCreated={(d) => { setCreating(false); navigate(`/notaire/dossiers/${d.id}`); }}
                />
            </main>
        </ReseauLayout>
    );
}
